/**
 * One roof, more than one material.
 *
 * Jonathon, 2026-09-16: "it also needs to ask if any portion of the roof is
 * metal when creating from measurements and which areas are so we can then
 * enter the measurements specific to metal. those measurements will be
 * subtracted from the rest of the measurements so that we don't double charge.
 * meaning some roofs have shingles and metal etc on the same roof."
 *
 * A porch in AG panel under a shingle house, a flat dormer in TPO, a metal
 * lean-to on the back — one building, measured once, sold as one roof and
 * bought as two or three. The measuring service reports the whole thing: 81
 * squares, 444 feet of eave, one number each. If the metal porch is then
 * priced as well, the shingle package is still buying shingles for it, and the
 * customer is charged for the same 8 squares twice.
 *
 * So a section is described here — what it is made of, which template covers
 * it, and the measurements that belong to *it* — and two things follow:
 *
 *   - `roofsWithout` takes those measurements off the roof they came out of,
 *     which is what the rest of the house is then generated from;
 *   - `sectionRoof` hands the section's own measurements over as a roof in its
 *     own right, so its template generates against the porch rather than
 *     against the house.
 *
 * Both are arithmetic and both are the reason the numbers add up, so they live
 * here rather than in the estimate builder (CLAUDE.md rule 5). Nothing here
 * writes anything: the caller generates a package per section against what
 * comes back.
 *
 * The measured roof itself is never edited. The building is measured once
 * (ADR 0030's `job_measurements` exception) and what a section changes is one
 * estimate, not the survey — a rep who describes the porch wrongly has written
 * a wrong estimate, not a wrong roof.
 */

import { ROOF_LAYOUT_FIELDS, type RoofLayoutField } from "./constants.js";
import type { RoofMeasurements } from "./generator.js";
import type { RoofPath } from "./templateReadiness.js";

/**
 * What a section is made of.
 *
 * The same three the estimate is sold down, because a section is generated
 * from a template and templates are offered by path. A shingle section is
 * allowed and is not silly: a shingle house with a shingle addition priced off
 * a different package is the same shape of problem.
 */
/**
 * A section is a piece of *roof* in another material, so siding and decks —
 * paths on the estimate screen, but a wall and a deck — are not choices.
 */
export type SectionMaterial = Exclude<RoofPath, "Siding" | "Decks">;

/** The materials a section can be in, for a dropdown. */
export const SECTION_MATERIALS: readonly SectionMaterial[] = [
  "Shingle",
  "Metal",
  "Flat & Other",
];

/** A part of the roof in a material of its own. */
export interface RoofSection {
  /** Only ever a key for the screen — sections are not stored anywhere. */
  id: string;
  /** What the rep calls it: "Front porch", "Back lean-to". */
  name: string;
  material: SectionMaterial;
  /** The template that covers it. Nothing is generated without one. */
  templateId: string;
  /** Which measured layout it was measured out of, when the job has several. */
  layoutId: string | null;
  /** Its own measurements, by `job_measurements` column. */
  values: Record<string, number | null>;
}

/**
 * The boxes a section asks for, by material.
 *
 * Deliberately not all twenty. A rep pulling a porch out of a report is
 * reading four or five numbers off a diagram, and a form of twenty boxes gets
 * four filled in and the rest left blank — which is the same thing as zero and
 * silently orders no drip edge. The labels come off `ROOF_LAYOUT_FIELDS` so a
 * box here and the same box on the measurement form always read alike.
 */
const SECTION_KEYS: Record<SectionMaterial, readonly string[]> = {
  Shingle: [
    "squares",
    "eave_lf",
    "rake_lf",
    "ridge_lf",
    "hip_lf",
    "valley_lf",
    "step_flashing_lf",
    "flashing_lf",
  ],
  Metal: [
    "squares",
    "eave_lf",
    "rake_lf",
    "ridge_lf",
    "hip_lf",
    "valley_lf",
    "step_flashing_lf",
    "flashing_lf",
  ],
  // A membrane roof is measured flat, and its perimeter is two numbers that
  // must not overlap — the open edge, and the parapet. Same rule as the
  // measurement form; see the note on `wall_length_lf` in constants.ts.
  "Flat & Other": [
    "flat_squares",
    "flat_perimeter_lf",
    "eave_lf",
    "rake_lf",
    "wall_length_lf",
    "wall_height_ft",
  ],
};

export function sectionFields(material: SectionMaterial): RoofLayoutField[] {
  const wanted = SECTION_KEYS[material] ?? [];
  return wanted
    .map((key) => ROOF_LAYOUT_FIELDS.find((field) => field.key === key))
    .filter((field): field is RoofLayoutField => field != null);
}

/**
 * The columns a section takes off the roof it came out of.
 *
 * Every measurement that is a quantity. `wall_height_ft` is not — it is how
 * tall the parapet is, and subtracting a height from a height is meaningless —
 * and neither are the pitch, the waste allowance or the layer count, which
 * describe the roof rather than measure it.
 */
const SUBTRACTED_KEYS: readonly string[] = [
  "squares",
  "flat_squares",
  "two_story_squares",
  "steep_mid_squares",
  "steep_high_squares",
  "eave_lf",
  "rake_lf",
  "ridge_lf",
  "ridge_vent_lf",
  "hip_lf",
  "valley_lf",
  "gutter_lf",
  "downspout_ea",
  "downspout_lf",
  "step_flashing_lf",
  "flashing_lf",
  "transition_lf",
  "flat_perimeter_lf",
  "wall_length_lf",
  "penetrations",
];

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const round = (n: number): number => Math.round(n * 100) / 100;

/**
 * A section's measurements, limited to the boxes its material asks for.
 *
 * Switching an area from Metal to Flat & Other hides the pitched boxes but
 * does not empty them, and a hidden 10 in `squares` used to go on being taken
 * off the house and handed to the TPO package. Only what the rep can see
 * counts.
 */
export function sectionValues(section: RoofSection): Record<string, number> {
  const allowed = SECTION_KEYS[section.material] ?? [];
  const out: Record<string, number> = {};
  for (const key of allowed) {
    const value = section.values[key];
    if (value != null && Number.isFinite(Number(value))) {
      out[key] = Number(value);
    }
  }
  return out;
}

/** A section with nothing measured on it generates nothing and is ignored. */
export function sectionIsEmpty(section: RoofSection): boolean {
  return !Object.values(sectionValues(section)).some((value) => value > 0);
}

/**
 * A section that will be generated: measured, and with a package chosen.
 *
 * Only these come off the rest of the roof. A section with no package is
 * never priced, so taking it off the house as well would sell nobody those
 * squares — and the screen and the generator both ask this one question, so
 * the figure the rep reads is the figure that is generated.
 */
export function sectionIsLive(section: RoofSection): boolean {
  return !sectionIsEmpty(section) && section.templateId !== "";
}

/** Which layout a section came out of — the named one, or the first. */
function homeOf<L extends { id: string }>(
  section: RoofSection,
  roofs: readonly L[],
): L | undefined {
  return roofs.find((roof) => roof.id === section.layoutId) ?? roofs[0];
}

/**
 * The roof a section's own package is generated against.
 *
 * Its measurements, on the pitch, the waste allowance and the layer count of
 * the roof it is part of. Those three are properties of the building: a metal
 * porch on a 16% waste report is cut out of the same roof, torn off the same
 * number of layers, and there is nothing else to learn them from.
 */
export function sectionRoof<L extends RoofMeasurements & { id: string }>(
  section: RoofSection,
  roofs: readonly L[],
): RoofMeasurements & { name: string } {
  const home = homeOf(section, roofs);
  return {
    ...sectionValues(section),
    name: section.name.trim() || "Section",
    average_pitch: (home as { average_pitch?: unknown })?.average_pitch ?? null,
    waste_pct: home?.waste_pct ?? null,
    layers: home?.layers ?? 1,
  } as RoofMeasurements & { name: string };
}

/**
 * How much each column comes off one roof, for the sections that are its.
 *
 * Column for column, with one exception: a flat area's squares come off the
 * roof's flat squares first and off its pitched squares after that. A
 * measuring service that does not split out the low-slope porch has counted
 * it in `squares`, and leaving it there sold it twice — once in the shingle
 * package and once in TPO — which is the thing this module exists to stop.
 */
function takenFrom(
  roof: Record<string, unknown>,
  mine: readonly RoofSection[],
): Record<string, number> {
  const taken: Record<string, number> = {};
  for (const key of SUBTRACTED_KEYS) {
    taken[key] = mine.reduce(
      (total, section) => total + num(sectionValues(section)[key]),
      0,
    );
  }
  const flatOver = taken.flat_squares! - num(roof.flat_squares);
  if (flatOver > 0) {
    taken.flat_squares = num(roof.flat_squares);
    taken.squares = taken.squares! + flatOver;
  }
  return taken;
}

/**
 * The roof that is left once the sections are taken out of it.
 *
 * Subtracted column for column, off the layout each section names, and floored
 * at zero — a negative eave is not a number anything downstream can use, and
 * where it would have gone negative `sectionWarnings` has already said so on
 * screen. Only live sections are taken off (`sectionIsLive`).
 *
 * Two columns follow the others rather than being entered:
 *   - a stored ridge vent is a share of the ridge, so it loses the same share
 *     of the ridge taken — otherwise the vent is bought twice. The share is
 *     the layout's own, not `RIDGE_VENT_SHARE_OF_RIDGE`: a roof measured
 *     while the rule was a third still stores a third (ADR 0239), and taking
 *     80% of the section's ridge off it would leave the rest of the roof with
 *     no ridge vent at all;
 *   - the steep and two-story bands are parts of `squares`, so none of them is
 *     left larger than the squares that remain.
 *
 * A layout no section points at comes back untouched, by identity, so a job
 * whose garage has no metal on it is generated from exactly what was measured.
 */
export function roofsWithout<L extends RoofMeasurements & { id: string }>(
  roofs: readonly L[],
  sections: readonly RoofSection[],
): L[] {
  const live = sections.filter(sectionIsLive);
  return roofs.map((roof) => {
    const mine = live.filter(
      (section) => homeOf(section, roofs)?.id === roof.id,
    );
    if (mine.length === 0) return roof;

    const before = roof as Record<string, unknown>;
    const next: Record<string, unknown> = { ...before };
    const taken = takenFrom(before, mine);
    for (const key of SUBTRACTED_KEYS) {
      if (taken[key]! <= 0) continue;
      next[key] = Math.max(0, round(num(before[key]) - taken[key]!));
    }

    if (
      taken.ridge_lf! > 0 &&
      num(before.ridge_lf) > 0 &&
      num(before.ridge_vent_lf) > 0
    ) {
      const share = num(before.ridge_vent_lf) / num(before.ridge_lf);
      next.ridge_vent_lf = Math.max(
        0,
        round(num(before.ridge_vent_lf) - taken.ridge_lf! * share),
      );
    }

    if (taken.squares! > 0) {
      const squares = num(next.squares);
      const high = Math.min(num(before.steep_high_squares), squares);
      const mid = Math.min(num(before.steep_mid_squares), squares - high);
      if (before.steep_high_squares != null) next.steep_high_squares = high;
      if (before.steep_mid_squares != null) next.steep_mid_squares = mid;
      if (before.two_story_squares != null) {
        next.two_story_squares = Math.min(
          num(before.two_story_squares),
          squares,
        );
      }
    }
    return next as L;
  });
}

/** How much roof a section is, in squares, whichever kind of roof it is. */
export function sectionSquares(section: RoofSection): number {
  const values = sectionValues(section);
  return round(num(values.squares) + num(values.flat_squares));
}

/**
 * What is wrong with the sections as described, in the rep's words.
 *
 * Warnings, never refusals. A section bigger than the roof it came out of is
 * usually a typo and occasionally a report that measured only the house while
 * the porch was measured by hand — the second one is legitimate, and only the
 * person looking at the building can tell them apart.
 */
export function sectionWarnings<L extends RoofMeasurements & { id: string }>(
  roofs: readonly L[],
  sections: readonly RoofSection[],
): string[] {
  const said: string[] = [];
  const live = sections.filter(sectionIsLive);

  for (const section of sections) {
    const name = section.name.trim() || "This area";
    if (sectionIsEmpty(section)) {
      said.push(
        `${name} has no measurements on it yet, so nothing will be generated for it.`,
      );
    } else if (!section.templateId) {
      said.push(
        `${name} has no package chosen, so nothing will be generated for it ` +
          `and nothing is taken off the rest of the roof.`,
      );
    }
  }

  for (const roof of roofs) {
    const mine = live.filter(
      (section) => homeOf(section, roofs)?.id === roof.id,
    );
    if (mine.length === 0) continue;
    const before = roof as Record<string, unknown>;
    const taken = takenFrom(before, mine);
    for (const key of SUBTRACTED_KEYS) {
      if (taken[key]! <= 0) continue;
      const measured = num(before[key]);
      if (taken[key]! <= measured) continue;
      const label =
        ROOF_LAYOUT_FIELDS.find((field) => field.key === key)?.label ?? key;
      said.push(
        `The areas you have described come to ${round(taken[key]!)} of ${label.toLowerCase()} and ` +
          `${(roof as { name?: string | null }).name ?? "the roof"} measures ${round(measured)}. ` +
          `The rest of the roof will be generated from none.`,
      );
    }
  }

  return said;
}
